import { ReactComponent as InfoIcon } from "../../../assets/svg/info.svg";
import { floorToDecimalsString } from "../../../utils/decimalString";
import { Skeleton } from "../../shared/Skeleton";
import {
  LeverageInfoRowItem,
  LeverageInfoSection,
  LeverageNoticeBox,
} from "./style";

interface ILeverageModalInfoProps {
  maxPositionValue?: string | number;
  availableBalance?: string;
  /** Set when the market's max leverage is unknown and changes are disabled. */
  unavailableText?: string;
  /** Account not loaded yet: show a placeholder, not a false "0.00 USD". */
  balanceLoading?: boolean;
}

/**
 * What an unknown figure reads as in these rows.
 *
 * One constant rather than three literals, because the three rows disagreed:
 * two dashed and "Available Balance" alone printed a confident "0.00 USD" once
 * loading finished without a balance -- i.e. when `/account` failed or returned
 * 401. The `balanceLoading` skeleton and its test covered only the LOADING
 * case, so the half of the bug that shows a trader "$0.00" before they pick a
 * leverage went unnoticed.
 *
 * It is an em-dash here and `UNKNOWN_DISPLAY` ("-") elsewhere in this same
 * form. DESIGN.md does not say which is right, so this keeps the character
 * these rows already use rather than inventing an answer; when the app settles
 * on one marker, this is the single line to change.
 */
const UNKNOWN_ROW_VALUE = "—";

/** Info rows and notices of the leverage modal (split out for file size). */
export function LeverageModalInfo({
  maxPositionValue,
  availableBalance,
  unavailableText,
  balanceLoading,
}: ILeverageModalInfoProps) {
  return (
    <>
      <LeverageInfoSection>
        <LeverageInfoRowItem>
          <span>Max Position Value</span>
          <span>
            {maxPositionValue
              ? `${Number(maxPositionValue).toLocaleString()} USD`
              : UNKNOWN_ROW_VALUE}
          </span>
        </LeverageInfoRowItem>
        <LeverageInfoRowItem>
          <span>Required Initial Margin</span>
          <span>{UNKNOWN_ROW_VALUE}</span>
        </LeverageInfoRowItem>
        <LeverageInfoRowItem>
          <span>Available Balance</span>
          <span>
            {balanceLoading ? (
              <Skeleton width={64} height={12} />
            ) : availableBalance ? (
              `${floorToDecimalsString(availableBalance, 2)} USD`
            ) : (
              UNKNOWN_ROW_VALUE
            )}
          </span>
        </LeverageInfoRowItem>
      </LeverageInfoSection>

      {unavailableText ? (
        <LeverageNoticeBox role="status" data-testid="max-leverage-unavailable">
          <InfoIcon />
          <span>{unavailableText}</span>
        </LeverageNoticeBox>
      ) : null}

      <LeverageNoticeBox>
        <InfoIcon />
        <span>
          Modifying your leverage will impact the leverage of your open
          position and orders.
        </span>
      </LeverageNoticeBox>
    </>
  );
}
